import { QuestionAnsweringTaskGroup } from "@/types/questionAnswering"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import apiClient from "./apiClient"
import {
  isQuestionnaireNotFound,
  useQuestionnaireRetry,
} from "./questionnaireQueryPolicy"

export const questionAnsweringTaskGroupApiCalls = {
  getTaskGroups: async (
    questionnaireId: string | undefined,
    active = true,
  ): Promise<QuestionAnsweringTaskGroup[]> => {
    if (!questionnaireId) {
      throw Error("Questionnaire ID is missing.")
    }

    const params = active ? { active: true } : {}
    const response = await apiClient.get<QuestionAnsweringTaskGroup[]>(
      `/questionnaires/${questionnaireId}/task-groups`,
      { params },
    )
    return response.data
  },
  getTaskGroup: async (
    questionnaireId: string | undefined,
    taskGroupId: string | undefined,
    signal?: AbortSignal,
  ): Promise<QuestionAnsweringTaskGroup> => {
    if (!questionnaireId || !taskGroupId) {
      throw new Error("Missing required IDs")
    }

    const response = await apiClient.get<QuestionAnsweringTaskGroup>(
      `/questionnaires/${questionnaireId}/task-groups/${taskGroupId}`,
      { signal },
    )
    return response.data
  },
}

export const useGetQuestionAnsweringTaskGroups = (
  questionnaireId: string | undefined,
  active = true,
) => {
  return useQuery({
    queryKey: ["questionAnsweringTaskGroups", { questionnaireId, active }],
    queryFn: () =>
      questionAnsweringTaskGroupApiCalls.getTaskGroups(questionnaireId, active),
    enabled: !!questionnaireId,
  })
}

export const usePollAutofillStatus = (
  questionnaireId: string | undefined,
  taskGroupId: string | undefined,
  refetchIntervalDuration = 3000,
  enabled = true,
) => {
  const [isTimeout] = useState(false)

  const retry = useQuestionnaireRetry()
  const query = useQuery({
    queryKey: ["taskGroup", { taskGroupId, questionnaireId }],
    queryFn: ({ signal }) =>
      questionAnsweringTaskGroupApiCalls.getTaskGroup(
        questionnaireId,
        taskGroupId,
        signal,
      ),
    refetchInterval: (query) => {
      if (isTimeout || isQuestionnaireNotFound(query.state.error)) return false

      const data = query.state.data
      if (data?.processed) {
        return false
      }
      return refetchIntervalDuration
    },
    enabled: enabled && !isTimeout && !!questionnaireId && !!taskGroupId,
    retry,
  })

  return { ...query, isTimeout }
}
